import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  FaYoutube, FaInstagram, FaTiktok, FaLinkedin, FaTwitter, FaFacebookF,
  FaPlus, FaSync, FaSignOutAlt, FaShareAlt, FaCheckCircle, FaTimesCircle, FaSpinner,
  FaCloudDownloadAlt, FaDownload, FaLayerGroup, FaList,
  FaVideo, FaImage, FaFont, FaThLarge,
} from 'react-icons/fa';
import { FaAt } from 'react-icons/fa6';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_META = {
  youtube:   { label: 'YouTube',   color: '#ff0000', Icon: FaYoutube,   supports: ['video'] },
  instagram: { label: 'Instagram', color: '#c13584', Icon: FaInstagram, supports: ['image', 'video'] },
  tiktok:    { label: 'TikTok',    color: '#000000', Icon: FaTiktok,    supports: ['video'] },
  linkedin:  { label: 'LinkedIn',  color: '#0a66c2', Icon: FaLinkedin,  supports: ['text', 'image', 'video'] },
  twitter:   { label: 'X',         color: '#000000', Icon: FaTwitter,   supports: ['text', 'image', 'video'] },
  threads:   { label: 'Threads',   color: '#000000', Icon: FaAt,        supports: ['text', 'image', 'video'] },
  facebook:  { label: 'Facebook',  color: '#000000', Icon: FaFacebookF, supports: ['text', 'image', 'video'] },
};

export default function Dashboard({ userId, setUserId }) {
  const [videos, setVideos] = useState([]);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [accountNames, setAccountNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [groupedView, setGroupedView] = useState(true); // All Content: grouped = deduped, ungrouped = all

  // Cross-post from library
  const [crossPostVideo, setCrossPostVideo] = useState(null);
  const [crossPostOpen, setCrossPostOpen] = useState(false);
  const [crossPostPlatforms, setCrossPostPlatforms] = useState([]);
  const [crossPostCaption, setCrossPostCaption] = useState('');
  const [crossPostFile, setCrossPostFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const [postResults, setPostResults] = useState([]);
  const [postedPlatforms, setPostedPlatforms] = useState({}); // { platform: posted_at }
  const crossPostFileRef = useRef(null);

  // Upload & Post new content
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadContentType, setUploadContentType] = useState('video'); // 'text' | 'image' | 'video'
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadPlatforms, setUploadPlatforms] = useState([]);
  const [uploadVideoUrl, setUploadVideoUrl] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadImageFile, setUploadImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const uploadFileRef = useRef(null);
  const uploadImageRef = useRef(null);
  const [linkedInOrgs, setLinkedInOrgs] = useState([]);
  const [linkedInDefaultTarget, setLinkedInDefaultTarget] = useState('personal');
  const [uploadLinkedInTarget, setUploadLinkedInTarget] = useState('personal');
  const [crossPostLinkedInTarget, setCrossPostLinkedInTarget] = useState('personal');
  const [refreshingLinkedInOrgs, setRefreshingLinkedInOrgs] = useState(false);
  const [addLinkedInOrgInput, setAddLinkedInOrgInput] = useState('');
  const [addingLinkedInOrg, setAddingLinkedInOrg] = useState(false);

  // Download state
  const [downloadingVideos, setDownloadingVideos] = useState(new Set());

  // Connect more accounts dialog
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);

  // YouTube cookies (fallback)
  const [ytCookiesPresent, setYtCookiesPresent] = useState(false);
  const [ytCookiesUploading, setYtCookiesUploading] = useState(false);
  const ytCookiesRef = useRef(null);

  // yt-dlp OAuth2 device flow
  const [ytDlpAuthStatus, setYtDlpAuthStatus] = useState('unknown'); // 'unknown' | 'authorized' | 'pending'
  const [ytDlpDeviceUrl, setYtDlpDeviceUrl] = useState(null);
  const [ytDlpDeviceCode, setYtDlpDeviceCode] = useState(null);
  const [ytDlpStarting, setYtDlpStarting] = useState(false);
  const ytDlpPollRef = useRef(null);

  const handleDisconnectPlatform = async (platform) => {
    try {
      await axios.delete(`${API}/auth/disconnect/${platform}/${userId}`);
      setConnectedPlatforms(prev => prev.filter(p => p !== platform));
      toast.success(`${PLATFORM_META[platform]?.label || platform} disconnected`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to disconnect ${platform}`);
    }
  };

  const handleConnectPlatform = async (platform) => {
    setConnectingPlatform(platform);
    try {
      const response = await axios.get(`${API}/auth/${platform}/login`, { params: userId ? { user_id: userId } : {} });
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else if (response.data.error) {
        toast.error(response.data.error, { duration: 6000 });
        setConnectingPlatform(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || `Failed to connect to ${platform}`;
      toast.error(errorMsg, { duration: 6000 });
      setConnectingPlatform(null);
    }
  };

  const handleDownloadVideo = async (video) => {
    if (downloadingVideos.has(video.id)) return;
    setDownloadingVideos(prev => new Set([...prev, video.id]));
    try {
      const res = await axios.post(`${API}/videos/${video.id}/download`, null, { params: { user_id: userId } });
      const status = res.data.status;
      if (status === 'already_downloaded') {
        toast.success('Already downloaded');
      } else {
        toast.success('Video downloaded');
      }
      // Refresh video list to reflect downloaded state
      const updated = await axios.get(`${API}/posts/${userId}`);
      setVideos(updated.data.videos || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingVideos(prev => { const s = new Set(prev); s.delete(video.id); return s; });
    }
  };

  const getAvailablePlatforms = () => {
    return Object.keys(PLATFORM_META).filter(p => !connectedPlatforms.includes(p));
  };

  useEffect(() => {
    if (userId) {
      fetchConnectedPlatforms();
      fetchPosts();
      fetchLinkedInOrgs();
      fetchYtCookiesStatus();
      fetchYtDlpAuthStatus();
    }
  }, [userId]);

  const fetchLinkedInOrgs = async () => {
    try {
      const response = await axios.get(`${API}/auth/linkedin/organizations?user_id=${userId}`);
      const orgs = response.data.organizations || [];
      const defaultTarget = response.data.default_target || 'personal';
      setLinkedInOrgs(orgs);
      setLinkedInDefaultTarget(defaultTarget);
      setUploadLinkedInTarget(defaultTarget);
      setCrossPostLinkedInTarget(defaultTarget);
    } catch (error) {
      console.error('Failed to fetch LinkedIn organizations', error);
    }
  };

  const handleRefreshLinkedInOrgs = async () => {
    setRefreshingLinkedInOrgs(true);
    try {
      const response = await axios.post(`${API}/auth/linkedin/refresh-orgs?user_id=${userId}`);
      setLinkedInOrgs(response.data.organizations || []);
      toast.success(`Found ${(response.data.organizations || []).length} LinkedIn organization(s)`);
    } catch (error) {
      toast.error('Failed to refresh LinkedIn organizations');
    } finally {
      setRefreshingLinkedInOrgs(false);
    }
  };

  const handleSetLinkedInDefaultTarget = async (target) => {
    setLinkedInDefaultTarget(target);
    setUploadLinkedInTarget(target);
    setCrossPostLinkedInTarget(target);
    try {
      await axios.post(`${API}/auth/linkedin/set-default-target?user_id=${userId}&target=${target}`);
    } catch (error) {
      console.error('Failed to save LinkedIn default target', error);
    }
  };

  const handleAddLinkedInOrg = async () => {
    const raw = addLinkedInOrgInput.trim();
    if (!raw) return;
    // Accept full URL like linkedin.com/company/roscode-tech or just the slug
    const vanity = raw.replace(/.*linkedin\.com\/company\//, '').replace(/\/.*$/, '').trim();
    setAddingLinkedInOrg(true);
    try {
      const response = await axios.post(`${API}/auth/linkedin/add-org?user_id=${userId}&vanity_name=${encodeURIComponent(vanity)}`);
      setLinkedInOrgs(response.data.organizations || []);
      setAddLinkedInOrgInput('');
      toast.success(`Added ${response.data.org?.name || vanity}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not find that LinkedIn company page');
    } finally {
      setAddingLinkedInOrg(false);
    }
  };

  const handleRemoveLinkedInOrg = async (orgId) => {
    try {
      const response = await axios.delete(`${API}/auth/linkedin/remove-org?user_id=${userId}&org_id=${encodeURIComponent(orgId)}`);
      setLinkedInOrgs(response.data.organizations || []);
      if (linkedInDefaultTarget === orgId) handleSetLinkedInDefaultTarget('personal');
    } catch (error) {
      toast.error('Failed to remove organization');
    }
  };

  const fetchConnectedPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/auth/connected/${userId}`);
      setConnectedPlatforms(response.data.connected_platforms || []);
      setAccountNames(response.data.account_names || {});
    } catch (error) {
      console.error('Failed to fetch connected platforms', error);
    }
  };

  const fetchYtCookiesStatus = async () => {
    try {
      const res = await axios.get(`${API}/settings/youtube-cookies`);
      setYtCookiesPresent(res.data.present);
    } catch {}
  };

  const fetchYtDlpAuthStatus = async () => {
    try {
      const res = await axios.get(`${API}/settings/yt-dlp-auth/status`);
      setYtDlpAuthStatus(res.data.status);
      return res.data.status;
    } catch {
      return 'unknown';
    }
  };

  const startYtDlpAuth = async () => {
    setYtDlpStarting(true);
    setYtDlpDeviceUrl(null);
    setYtDlpDeviceCode(null);
    try {
      const res = await axios.post(`${API}/settings/yt-dlp-auth/start`);
      if (res.data.status === 'error') { toast.error(res.data.detail); return; }
      setYtDlpDeviceUrl(res.data.device_url);
      setYtDlpDeviceCode(res.data.device_code);
      setYtDlpAuthStatus('pending');
      // Poll for completion
      if (ytDlpPollRef.current) clearInterval(ytDlpPollRef.current);
      ytDlpPollRef.current = setInterval(async () => {
        const status = await fetchYtDlpAuthStatus();
        if (status === 'authorized') {
          clearInterval(ytDlpPollRef.current);
          setYtDlpDeviceUrl(null);
          setYtDlpDeviceCode(null);
          toast.success('YouTube downloads authorized! You can now download any video.');
        }
      }, 4000);
    } catch {
      toast.error('Failed to start YouTube auth');
    } finally {
      setYtDlpStarting(false);
    }
  };

  const revokeYtDlpAuth = async () => {
    try {
      await axios.delete(`${API}/settings/yt-dlp-auth`);
      setYtDlpAuthStatus('unknown');
      toast.success('YouTube download auth revoked');
    } catch { toast.error('Failed to revoke'); }
  };

  const handleYtCookiesUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setYtCookiesUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/settings/youtube-cookies`, fd);
      setYtCookiesPresent(true);
      toast.success('YouTube cookies saved — downloads should work now');
    } catch {
      toast.error('Failed to upload cookies file');
    } finally {
      setYtCookiesUploading(false);
      if (ytCookiesRef.current) ytCookiesRef.current.value = '';
    }
  };

  const handleYtCookiesDelete = async () => {
    try {
      await axios.delete(`${API}/settings/youtube-cookies`);
      setYtCookiesPresent(false);
      toast.success('YouTube cookies removed');
    } catch {
      toast.error('Failed to remove cookies');
    }
  };

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/videos/${userId}`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Failed to load videos', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/posts/${userId}`);
      setVideos(response.data.posts || []);
    } catch (error) {
      console.error('Failed to load posts', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = useMemo(() => {
    if (activeTab === 'all') {
      return groupedView ? videos.filter(v => !v.is_cross_post_target) : videos;
    }
    const noSyncPlatforms = ['linkedin', 'twitter', 'threads'];
    if (noSyncPlatforms.includes(activeTab)) {
      return videos.filter(v =>
        v.platform === activeTab ||
        (v.posted_platforms || []).some(p => p.platform === activeTab)
      );
    }
    return videos.filter(v => v.platform === activeTab);
  }, [activeTab, videos, groupedView]);

  const syncPlatform = async (platform) => {
    setSyncing(true);
    try {
      await axios.post(`${API}/videos/sync/${platform}?user_id=${userId}`);
      toast.success(`${platform} synced`);
      fetchPosts();
    } catch (error) {
      const msg = error.response?.data?.detail || `Failed to sync ${platform}`;
      const isAuth = error.response?.status === 401;
      toast.error(msg, {
        duration: isAuth ? 8000 : 4000,
        description: isAuth ? 'Go to Connect Accounts to reconnect.' : undefined,
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userId');
    setUserId(null);
  };

  // Determine the effective content type of a post/video for platform compatibility checks
  const getContentType = (video) => {
    const t = video.post_type;
    if (!t || t === 'video') return 'video';
    if (t === 'carousel') return 'image'; // carousels treated as image for support checks
    if (t === 'image') return 'image';
    return 'text';
  };

  const CONTENT_TYPE_META = {
    video:   { label: 'Video',    Icon: FaVideo,   color: '#fff', bg: '#ef4444' },
    image:   { label: 'Image',    Icon: FaImage,   color: '#fff', bg: '#3b82f6' },
    text:    { label: 'Text',     Icon: FaFont,    color: '#fff', bg: '#8b5cf6' },
    carousel:{ label: 'Carousel', Icon: FaThLarge, color: '#fff', bg: '#3b82f6' },
  };

  const getPlatformIcon = (platform) => {
    const meta = PLATFORM_META[platform];
    if (!meta) return null;
    const { Icon } = meta;
    return <Icon className={`platform-badge-icon ${platform}`} />;
  };

  const findDuplicates = (video) => {
    if (!video.perceptual_hash) return [];
    return videos.filter(v => v.id !== video.id && v.perceptual_hash === video.perceptual_hash);
  };

  // ─── Cross-Post from Library ────────────────────────────────────────────────

  const openCrossPost = (video) => {
    setCrossPostVideo(video);
    const titlePart = video.title || video.text || '';
    const descPart = video.description || (!video.title ? '' : video.text || '');
    setCrossPostCaption(`${titlePart}\n\n${descPart}`.trim());
    setCrossPostPlatforms([]);
    setCrossPostFile(null);
    setPostResults([]);
    // Build posted-platforms map from already-loaded video data.
    // LinkedIn entries use key "linkedin:{accountId}" to track per-account.
    // All entries are objects: { posted_at, url, account_name? }
    const map = {};
    for (const p of (video.posted_platforms || [])) {
      if (p.platform === 'linkedin' && p.account_id) {
        map[`linkedin:${p.account_id}`] = { posted_at: p.posted_at, url: p.url || '', account_name: p.account_name };
      } else {
        map[p.platform] = { posted_at: p.posted_at, url: p.url || '' };
      }
    }
    setPostedPlatforms(map);
    setCrossPostLinkedInTarget(linkedInDefaultTarget);
    setCrossPostOpen(true);
  };

  const toggleCrossPostPlatform = (platform) => {
    setCrossPostPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const handleCrossPost = async (overrideDuplicates = false) => {
    if (crossPostPlatforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    setPosting(true);
    setPostResults([]);
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('title', crossPostVideo.title);
      formData.append('description', crossPostCaption || crossPostVideo.description || '');
      formData.append('platforms', JSON.stringify(crossPostPlatforms));
      formData.append('content_type', 'video');
      formData.append('override_duplicates', overrideDuplicates ? 'true' : 'false');
      if (crossPostVideo.id) formData.append('video_id', crossPostVideo.id);
      if (crossPostFile) formData.append('video_file', crossPostFile);
      if (crossPostPlatforms.includes('linkedin')) formData.append('linkedin_org_id', crossPostLinkedInTarget);
      const response = await axios.post(`${API}/posts/crosspost`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPostResults(response.data.results || []);
      const failed = response.data.results.filter(r => r.status === 'error');
      const succeeded = response.data.results.filter(r => r.status === 'success');
      const duplicates = response.data.results.filter(r => r.status === 'duplicate');
      if (succeeded.length > 0) toast.success(`Posted to ${succeeded.map(r => r.platform).join(', ')}`);
      if (failed.length > 0) toast.error(`Failed: ${failed.map(r => r.platform).join(', ')}`);
      if (duplicates.length > 0 && succeeded.length === 0 && failed.length === 0) {
        toast(`Already posted to ${duplicates.map(r => r.platform).join(', ')} — use "Post Anyway" to repost`);
      }
    } catch (error) {
      toast.error('Cross-posting failed');
    } finally {
      setPosting(false);
    }
  };

  // ─── Upload & Post New ──────────────────────────────────────────────────────

  const toggleUploadPlatform = (platform) => {
    setUploadPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  // When content type changes, clear incompatible platform selections
  const handleContentTypeChange = (ct) => {
    setUploadContentType(ct);
    setUploadPlatforms(prev => prev.filter(p => PLATFORM_META[p]?.supports.includes(ct)));
    setUploadFile(null);
    setUploadImageFile(null);
    setUploadVideoUrl('');
  };

  const handleUploadPost = async () => {
    if (!uploadTitle.trim()) { toast.error('Title is required'); return; }
    if (uploadPlatforms.length === 0) { toast.error('Select at least one platform'); return; }

    if (uploadContentType === 'video') {
      const needsFile = uploadPlatforms.some(p => ['youtube', 'tiktok'].includes(p));
      if (needsFile && !uploadFile) { toast.error('YouTube and TikTok require a video file'); return; }
      const needsMedia = uploadPlatforms.some(p => ['instagram', 'threads', 'facebook'].includes(p));
      if (needsMedia && !uploadFile && !uploadVideoUrl.trim()) {
        toast.error('Instagram / Threads / Facebook require a video file or public URL');
        return;
      }
    }

    if (uploadContentType === 'image' && !uploadImageFile) {
      toast.error('Select an image file to upload');
      return;
    }

    setUploading(true);
    setUploadResults([]);
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('title', uploadTitle);
      formData.append('description', uploadDescription);
      formData.append('platforms', JSON.stringify(uploadPlatforms));
      formData.append('content_type', uploadContentType);
      if (uploadContentType === 'video') {
        if (uploadVideoUrl.trim()) formData.append('video_url', uploadVideoUrl.trim());
        if (uploadFile) formData.append('video_file', uploadFile);
      }
      if (uploadContentType === 'image' && uploadImageFile) {
        formData.append('image_file', uploadImageFile);
      }
      if (uploadPlatforms.includes('linkedin')) formData.append('linkedin_org_id', uploadLinkedInTarget);

      const response = await axios.post(`${API}/posts/crosspost`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResults(response.data.results || []);
      const failed = response.data.results.filter(r => r.status === 'error');
      const succeeded = response.data.results.filter(r => r.status === 'success');
      if (succeeded.length > 0) toast.success(`Posted to ${succeeded.map(r => r.platform).join(', ')}`);
      if (failed.length > 0) toast.error(`Failed on: ${failed.map(r => r.platform).join(', ')}`);
    } catch (error) {
      toast.error('Posting failed');
    } finally {
      setUploading(false);
    }
  };

  // ─── Render Helpers ─────────────────────────────────────────────────────────

  const PostResultList = ({ results, onPostAnyway }) => {
    const hasDuplicates = results.some(r => r.status === 'duplicate');
    return (
      <div className="post-results">
        {results.map(r => (
          <div key={r.platform} className={`post-result-item ${r.status}`}>
            <div className="result-left">
              {r.status === 'success'
                ? <FaCheckCircle className="result-icon success" />
                : r.status === 'duplicate'
                  ? <span className="result-icon duplicate">⟳</span>
                  : <FaTimesCircle className="result-icon error" />}
              <span className="result-platform">{PLATFORM_META[r.platform]?.label || r.platform}</span>
              {r.status === 'duplicate' && <span className="result-error">Already posted</span>}
              {r.status === 'error' && <span className="result-error">{r.error}</span>}
            </div>
            {r.status === 'success' && r.url && (
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="view-post-btn">
                View Post ↗
              </a>
            )}
          </div>
        ))}
        {hasDuplicates && onPostAnyway && (
          <Button size="sm" variant="outline" className="post-anyway-btn" onClick={onPostAnyway}>
            Post Anyway (override duplicates)
          </Button>
        )}
      </div>
    );
  };

  const MediaPreview = ({ video, ctMeta, allPlatforms }) => {
    const { Icon: CtIcon } = ctMeta;
    const effectiveType = getContentType(video);

    // Text posts: compact text-preview block, no aspect-ratio box
    if (effectiveType === 'text') {
      const previewText = video.text || video.description || video.title || '';
      return (
        <div className="media-preview media-preview--text">
          <div className="content-type-badge" style={{ background: ctMeta.bg, color: ctMeta.color, borderColor: ctMeta.bg }}>
            <CtIcon /><span>{ctMeta.label}</span>
          </div>
          <div className="platform-badges">
            {allPlatforms.map(p => (
              <span key={p} className={`badge ${p}`} data-testid={`video-platform-${p}`}>{getPlatformIcon(p)}</span>
            ))}
          </div>
          <p className="text-preview-content">
            {previewText.length > 180 ? previewText.substring(0, 180) + '…' : previewText || '(no caption)'}
          </p>
        </div>
      );
    }

    // Image / video: aspect-ratio thumbnail box
    return (
      <div className="media-preview media-preview--media">
        {video.thumbnail_url
          ? <img src={video.thumbnail_url} alt={video.title} />
          : <div className="thumbnail-placeholder">
              {effectiveType === 'image' && <CtIcon style={{ fontSize: '2rem', color: ctMeta.color, opacity: 0.4 }} />}
            </div>}
        <div className="content-type-badge" style={{ background: ctMeta.bg, color: ctMeta.color, borderColor: ctMeta.bg }}>
          <CtIcon /><span>{ctMeta.label}</span>
        </div>
        <div className="platform-badges">
          {allPlatforms.map(p => (
            <span key={p} className={`badge ${p}`} data-testid={`video-platform-${p}`}>{getPlatformIcon(p)}</span>
          ))}
        </div>
      </div>
    );
  };

  const ContentCard = ({ video }) => {
    const duplicates = findDuplicates(video);
    const crossPostedPlatforms = (video.posted_platforms || []).map(p => p.platform);
    const allPlatforms = [...new Set([video.platform, ...duplicates.map(d => d.platform), ...crossPostedPlatforms])];
    const contentType = video.post_type || 'video';
    const ctMeta = CONTENT_TYPE_META[contentType] || CONTENT_TYPE_META.video;
    const isVideo = getContentType(video) === 'video';

    return (
      <div className="video-card" data-testid="video-card">
        <MediaPreview video={video} ctMeta={ctMeta} allPlatforms={allPlatforms} />
        <div className="video-info">
          <h3 className="video-title" data-testid="video-title">{video.title || video.text}</h3>
          {video.description && (
            <p className="video-description">{video.description.substring(0, 100)}{video.description.length > 100 ? '…' : ''}</p>
          )}
          <div className="video-actions">
            {isVideo && (
              <Button
                size="sm"
                variant={video.downloaded ? 'outline' : 'ghost'}
                className={`download-btn ${video.downloaded ? 'downloaded' : ''}`}
                onClick={() => handleDownloadVideo(video)}
                disabled={downloadingVideos.has(video.id)}
                title={video.downloaded ? 'Downloaded' : 'Download video'}
              >
                {downloadingVideos.has(video.id)
                  ? <FaSpinner className="spin" />
                  : video.downloaded
                    ? <FaDownload style={{ color: '#22c55e' }} />
                    : <FaCloudDownloadAlt />}
              </Button>
            )}
            <Button
              size="sm"
              className="crosspost-btn"
              onClick={() => openCrossPost(video)}
            >
              <FaShareAlt /> Cross-Post
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title" data-testid="dashboard-title">Content Radar</h1>
            <div className="connected-platforms">
              {connectedPlatforms.map(platform => (
                <span key={platform} className={`platform-badge ${platform}`} data-testid={`connected-${platform}`}>
                  {getPlatformIcon(platform)}
                  {PLATFORM_META[platform]?.label || platform}
                </span>
              ))}
            </div>
          </div>
          <div className="header-right">
            {/* Connect More Accounts */}
            <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="connect-btn" data-testid="connect-btn">
                  <FaPlus /> Manage Accounts
                </Button>
              </DialogTrigger>
              <DialogContent className="connect-dialog">
                <DialogHeader>
                  <DialogTitle>Manage Accounts</DialogTitle>
                  <DialogDescription>
                    Connect platforms or reconnect to refresh an expired token
                  </DialogDescription>
                </DialogHeader>
                <div className="platform-connect-list">
                  {/* YouTube cookie upload */}
                  <div className="yt-cookies-section">
                    <div className="yt-cookies-header">
                      <span className="yt-cookies-title">YouTube Video Downloads</span>
                      {ytCookiesPresent
                        ? <span className="yt-cookies-badge present">Cookies Active</span>
                        : <span className="yt-cookies-badge missing">Not set up</span>}
                    </div>
                    <p className="yt-cookies-note">
                      YouTube requires browser cookies to allow downloads. Export yours safely using yt-dlp locally:
                    </p>
                    <div className="yt-dlp-device-flow">
                      <p className="yt-cookies-note" style={{ fontFamily: 'monospace', background: '#1a1a2e', color: '#a5f3fc', padding: '6px 10px', borderRadius: '6px', fontSize: '0.78rem' }}>
                        yt-dlp --cookies-from-browser chrome --cookies youtube_cookies.txt --skip-download "https://youtube.com"
                      </p>
                      <p className="yt-cookies-note">Then upload the generated <strong>youtube_cookies.txt</strong> below. Cookies expire after a few weeks — re-upload when downloads stop working.</p>
                    </div>
                    <div className="yt-cookies-actions">
                      <input ref={ytCookiesRef} type="file" accept=".txt" style={{ display: 'none' }} onChange={handleYtCookiesUpload} />
                      <Button size="sm" variant="outline" onClick={() => ytCookiesRef.current?.click()} disabled={ytCookiesUploading}>
                        {ytCookiesUploading ? 'Uploading…' : ytCookiesPresent ? 'Replace cookies.txt' : 'Upload cookies.txt'}
                      </Button>
                      {ytCookiesPresent && (
                        <Button size="sm" variant="outline" style={{ color: '#ef4444', borderColor: '#ef4444' }} onClick={handleYtCookiesDelete}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0' }} />
                  {Object.keys(PLATFORM_META).map(platform => {
                    const meta = PLATFORM_META[platform];
                    if (!meta) return null;
                    const { Icon } = meta;
                    const isConnected = connectedPlatforms.includes(platform);
                    return (
                      <div key={platform} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <Button
                            className={`platform-connect-btn ${platform}${isConnected ? ' reconnect' : ''}`}
                            onClick={() => handleConnectPlatform(platform)}
                            disabled={connectingPlatform !== null}
                            style={{ borderColor: meta.color, borderWidth: '2px', flex: 1 }}
                          >
                            <Icon style={{ color: meta.color === '#000000' ? '#ffffff' : meta.color, marginRight: '8px' }} />
                            {connectingPlatform === platform
                              ? 'Connecting…'
                              : isConnected
                                ? `Reconnect ${meta.label}`
                                : `Connect ${meta.label}`}
                            {isConnected && <span className="connected-indicator">✓</span>}
                          </Button>
                          {isConnected && (
                            <Button
                              variant="outline"
                              style={{ borderColor: '#ef4444', color: '#ef4444', padding: '0 12px' }}
                              onClick={() => handleDisconnectPlatform(platform)}
                              disabled={connectingPlatform !== null}
                            >
                              Disconnect
                            </Button>
                          )}
                        </div>
                        {isConnected && accountNames[platform] && (
                          <div style={{ fontSize: '12px', color: '#888', paddingLeft: '4px' }}>
                            Connected as: <strong>{accountNames[platform]}</strong>
                          </div>
                        )}
                        {isConnected && platform === 'linkedin' && (
                          <div className="linkedin-account-manager">
                            {/* Header */}
                            <div className="linkedin-manager-header">
                              <span className="linkedin-manager-header-label">LinkedIn Pages</span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="refresh-orgs-btn"
                                onClick={handleRefreshLinkedInOrgs}
                                disabled={refreshingLinkedInOrgs}
                                title="Auto-detect company pages"
                              >
                                {refreshingLinkedInOrgs ? <FaSpinner className="spinning" /> : <FaSync />}
                              </Button>
                            </div>

                            {/* Default target selector */}
                            <div className="linkedin-default-row">
                              <span className="linkedin-default-label">Default:</span>
                              <select
                                className="linkedin-target-select"
                                value={linkedInDefaultTarget}
                                onChange={e => handleSetLinkedInDefaultTarget(e.target.value)}
                              >
                                <option value="personal">👤 {accountNames['linkedin'] || 'Personal Profile'}</option>
                                {linkedInOrgs.map(org => (
                                  <option key={org.id} value={org.id}>🏢 {org.name}</option>
                                ))}
                              </select>
                            </div>

                            {/* Personal profile row (always present) */}
                            <div className="linkedin-org-list">
                              <div className="linkedin-org-item">
                                <span className="linkedin-org-icon">👤</span>
                                <span className="linkedin-org-name">{accountNames['linkedin'] || 'Personal Profile'}</span>
                                <span style={{ fontSize: '0.7rem', color: '#888' }}>Personal</span>
                              </div>
                              {linkedInOrgs.map(org => (
                                <div key={org.id} className="linkedin-org-item">
                                  <span className="linkedin-org-icon">🏢</span>
                                  <span className="linkedin-org-name">{org.name}</span>
                                  <button className="linkedin-org-remove" onClick={() => handleRemoveLinkedInOrg(org.id)} title="Remove">×</button>
                                </div>
                              ))}
                            </div>

                            {/* Add org */}
                            <div className="linkedin-add-org">
                              <input
                                className="linkedin-add-org-input"
                                placeholder="linkedin.com/company/your-company"
                                value={addLinkedInOrgInput}
                                onChange={e => setAddLinkedInOrgInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddLinkedInOrg()}
                              />
                              <Button
                                size="sm"
                                className="linkedin-add-btn"
                                onClick={handleAddLinkedInOrg}
                                disabled={addingLinkedInOrg || !addLinkedInOrgInput.trim()}
                              >
                                {addingLinkedInOrg ? <FaSpinner className="spinning" /> : <FaPlus />}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>

            {/* Upload & Post */}
            <Dialog open={uploadOpen} onOpenChange={(open) => {
              setUploadOpen(open);
              if (!open) {
                setUploadResults([]);
                setUploadTitle(''); setUploadDescription('');
                setUploadFile(null); setUploadImageFile(null); setUploadVideoUrl('');
                setUploadPlatforms([]); setUploadContentType('video');
              }
            }}>
              <DialogTrigger asChild>
                <Button className="upload-btn" data-testid="upload-btn">
                  <FaPlus /> Upload &amp; Post
                </Button>
              </DialogTrigger>
              <DialogContent className="upload-dialog">
                <DialogHeader>
                  <DialogTitle>Upload &amp; Post</DialogTitle>
                  <DialogDescription>Create and post content to your connected platforms</DialogDescription>
                </DialogHeader>
                {uploadResults.length > 0 ? (
                  <>
                    <PostResultList results={uploadResults} />
                    <Button onClick={() => {
                      setUploadResults([]);
                      setUploadTitle(''); setUploadDescription('');
                      setUploadFile(null); setUploadImageFile(null); setUploadVideoUrl('');
                      setUploadPlatforms([]); setUploadContentType('video');
                    }}>
                      Post Another
                    </Button>
                  </>
                ) : (
                  <div className="upload-form">

                    {/* Content type selector */}
                    <div className="form-group">
                      <Label>Content Type</Label>
                      <div className="content-type-toggle">
                        {['text', 'image', 'video'].map(ct => (
                          <button
                            key={ct}
                            type="button"
                            className={`content-type-btn${uploadContentType === ct ? ' active' : ''}`}
                            onClick={() => handleContentTypeChange(ct)}
                          >
                            {ct === 'text' ? '📝 Text' : ct === 'image' ? '🖼 Image' : '🎬 Video'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <Label htmlFor="upload-title">{uploadContentType === 'text' ? 'Post Text *' : 'Title *'}</Label>
                      <Input
                        id="upload-title"
                        placeholder={uploadContentType === 'text' ? 'Write your post…' : 'Title'}
                        value={uploadTitle}
                        onChange={e => setUploadTitle(e.target.value)}
                        data-testid="upload-title-input"
                      />
                    </div>

                    {uploadContentType !== 'text' && (
                      <div className="form-group">
                        <Label htmlFor="upload-description">Caption / Description</Label>
                        <Textarea
                          id="upload-description"
                          placeholder="Caption or description…"
                          value={uploadDescription}
                          onChange={e => setUploadDescription(e.target.value)}
                          rows={3}
                          data-testid="upload-description-input"
                        />
                      </div>
                    )}

                    {uploadContentType === 'text' && (
                      <div className="form-group">
                        <Label htmlFor="upload-description">Additional Text</Label>
                        <Textarea
                          id="upload-description"
                          placeholder="More text (optional)…"
                          value={uploadDescription}
                          onChange={e => setUploadDescription(e.target.value)}
                          rows={3}
                        />
                      </div>
                    )}

                    {uploadContentType === 'image' && (
                      <div className="form-group">
                        <Label>Image File *</Label>
                        <input
                          ref={uploadImageRef}
                          type="file"
                          accept="image/*"
                          className="file-input"
                          onChange={e => setUploadImageFile(e.target.files[0] || null)}
                        />
                        {uploadImageFile && <span className="file-name">{uploadImageFile.name}</span>}
                      </div>
                    )}

                    {uploadContentType === 'video' && (
                      <>
                        <div className="form-group">
                          <Label>Video File <span className="label-note">(required for YouTube &amp; TikTok)</span></Label>
                          <input
                            ref={uploadFileRef}
                            type="file"
                            accept="video/*"
                            className="file-input"
                            onChange={e => setUploadFile(e.target.files[0] || null)}
                          />
                          {uploadFile && <span className="file-name">{uploadFile.name}</span>}
                        </div>
                        <div className="form-group">
                          <Label htmlFor="upload-video-url">Public Video URL <span className="label-note">(Instagram / Threads / Facebook)</span></Label>
                          <Input
                            id="upload-video-url"
                            placeholder="https://…"
                            value={uploadVideoUrl}
                            onChange={e => setUploadVideoUrl(e.target.value)}
                          />
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <Label>Post to Platforms</Label>
                      <div className="platform-checkboxes">
                        {connectedPlatforms
                          .filter(p => PLATFORM_META[p]?.supports.includes(uploadContentType))
                          .map(platform => {
                            const meta = PLATFORM_META[platform];
                            if (!meta) return null;
                            const { Icon } = meta;
                            return (
                              <div key={platform} className="checkbox-item">
                                <Checkbox
                                  id={`upload-${platform}`}
                                  checked={uploadPlatforms.includes(platform)}
                                  onCheckedChange={() => toggleUploadPlatform(platform)}
                                  data-testid={`upload-${platform}-checkbox`}
                                />
                                <Label htmlFor={`upload-${platform}`} className="checkbox-label">
                                  <Icon style={{ color: meta.color }} /> {meta.label}
                                </Label>
                              </div>
                            );
                          })}
                        {connectedPlatforms.filter(p => PLATFORM_META[p]?.supports.includes(uploadContentType)).length === 0 && (
                          <p className="no-platforms">No connected platforms support {uploadContentType} posts.</p>
                        )}
                      </div>
                    </div>

                    {uploadPlatforms.includes('linkedin') && (
                      <div className="form-group">
                        <Label htmlFor="upload-linkedin-target">Post to LinkedIn as</Label>
                        <select
                          id="upload-linkedin-target"
                          className="linkedin-target-select"
                          value={uploadLinkedInTarget}
                          onChange={e => setUploadLinkedInTarget(e.target.value)}
                        >
                          <option value="personal">👤 Personal Profile</option>
                          {linkedInOrgs.map(org => (
                            <option key={org.id} value={org.id}>🏢 {org.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <Button
                      className="schedule-btn"
                      onClick={handleUploadPost}
                      disabled={uploading}
                      data-testid="schedule-post-btn"
                    >
                      {uploading ? <><FaSpinner className="spinning" /> Posting…</> : 'Post Now'}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
              <FaSignOutAlt /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Cross-Post Dialog (from library video) */}
      <Dialog open={crossPostOpen} onOpenChange={(open) => { setCrossPostOpen(open); if (!open) setPostResults([]); }}>
        <DialogContent className="upload-dialog">
          <DialogHeader>
            <DialogTitle>Cross-Post Video</DialogTitle>
            <DialogDescription>
              Post this video to additional platforms
            </DialogDescription>
          </DialogHeader>
          {crossPostVideo && (
            postResults.length > 0 ? (
              <>
                <PostResultList results={postResults} onPostAnyway={() => handleCrossPost(true)} />
                <Button onClick={() => setCrossPostOpen(false)}>Done</Button>
              </>
            ) : (
              <div className="upload-form">
                <div className="crosspost-source">
                  {crossPostVideo.thumbnail_url && (
                    <img src={crossPostVideo.thumbnail_url} alt={crossPostVideo.title} className="crosspost-thumb" />
                  )}
                  <div>
                    <p className="crosspost-source-title">{crossPostVideo.title}</p>
                    <span className={`badge-sm ${crossPostVideo.platform}`}>
                      {getPlatformIcon(crossPostVideo.platform)} {crossPostVideo.platform}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="crosspost-caption">Caption</Label>
                  <Textarea
                    id="crosspost-caption"
                    value={crossPostCaption}
                    onChange={e => setCrossPostCaption(e.target.value)}
                    rows={4}
                    placeholder="Caption for the post…"
                  />
                </div>

                <div className="form-group">
                  <Label>Override with file <span className="label-note">(optional — needed if URL is private)</span></Label>
                  <input
                    ref={crossPostFileRef}
                    type="file"
                    accept="video/*"
                    className="file-input"
                    onChange={e => setCrossPostFile(e.target.files[0] || null)}
                  />
                  {crossPostFile && <span className="file-name">{crossPostFile.name}</span>}
                </div>

                <div className="form-group">
                  <Label>Post to Platforms</Label>
                  <div className="platform-checkboxes">
                    {connectedPlatforms
                      .filter(p => p !== crossPostVideo.platform)
                      .map(platform => {
                        const meta = PLATFORM_META[platform];
                        if (!meta) return null;
                        const { Icon } = meta;
                        const effectiveType = getContentType(crossPostVideo);
                        const supported = meta.supports.includes(effectiveType);
                        const needsFileButNone = meta.needsFile && !crossPostFile;
                        const isChecked = crossPostPlatforms.includes(platform);

                        // For LinkedIn, "already posted" depends on the selected account
                        const alreadyPosted = platform === 'linkedin'
                          ? postedPlatforms[`linkedin:${crossPostLinkedInTarget}`]
                          : postedPlatforms[platform];
                        const alreadyPostedDate = alreadyPosted?.posted_at;
                        const alreadyPostedUrl = alreadyPosted?.url;

                        return (
                          <div key={platform} className={`checkbox-item${alreadyPosted ? ' already-posted' : ''}${!supported ? ' unsupported' : ''}`}>
                            <Checkbox
                              id={`cp-${platform}`}
                              checked={isChecked && supported}
                              disabled={!supported}
                              onCheckedChange={() => supported && toggleCrossPostPlatform(platform)}
                            />
                            <Label htmlFor={`cp-${platform}`} className={`checkbox-label${!supported ? ' unsupported-label' : ''}`}>
                              <Icon style={{ color: supported ? meta.color : '#999' }} /> {meta.label}
                              {!supported && (
                                <span className="unsupported-note">doesn't support {effectiveType}</span>
                              )}
                              {supported && needsFileButNone && (
                                <span className="label-note"> (upload file above)</span>
                              )}
                              {alreadyPosted && (
                                <span className="already-posted-badge">
                                  {platform === 'linkedin'
                                    ? `✓ Posted to ${crossPostLinkedInTarget === 'personal' ? 'personal profile' : 'business page'} "${alreadyPosted.account_name}" on ${new Date(alreadyPostedDate).toLocaleDateString()}`
                                    : `✓ Posted ${new Date(alreadyPostedDate).toLocaleDateString()}`}
                                  {alreadyPostedUrl && (
                                    <a href={alreadyPostedUrl} target="_blank" rel="noopener noreferrer" className="already-posted-view-btn" onClick={e => e.stopPropagation()}>View ↗</a>
                                  )}
                                </span>
                              )}
                            </Label>
                            {alreadyPosted && isChecked && (
                              <span className="repost-warning">
                                {platform === 'linkedin'
                                  ? `⚠ Will repost to "${alreadyPosted.account_name}"`
                                  : '⚠ Will repost'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  {crossPostPlatforms.includes('linkedin') && (
                    <div className="form-group" style={{ marginTop: '8px' }}>
                      <Label htmlFor="cp-linkedin-target">Post to LinkedIn as</Label>
                      <select
                        id="cp-linkedin-target"
                        className="linkedin-target-select"
                        value={crossPostLinkedInTarget}
                        onChange={e => setCrossPostLinkedInTarget(e.target.value)}
                      >
                        <option value="personal">
                          👤 {accountNames['linkedin'] || 'Personal Profile'}{postedPlatforms['linkedin:personal'] ? ' ✓' : ''}
                        </option>
                        {linkedInOrgs.map(org => (
                          <option key={org.id} value={org.id}>
                            🏢 {org.name}{postedPlatforms[`linkedin:${org.id}`] ? ' ✓' : ''}
                          </option>
                        ))}
                      </select>
                      {(() => {
                        const allPosted = [
                          postedPlatforms['linkedin:personal'] && `personal profile "${postedPlatforms['linkedin:personal'].account_name || accountNames['linkedin'] || 'Personal'}"`,
                          ...linkedInOrgs.map(o => postedPlatforms[`linkedin:${o.id}`] && `business page "${o.name}"`),
                        ].filter(Boolean);
                        return allPosted.length > 0 ? (
                          <p className="linkedin-posted-summary">
                            Already posted to: {allPosted.join(', ')}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  )}
                  {connectedPlatforms.filter(p => p !== crossPostVideo?.platform).length === 0 && (
                    <p className="no-platforms">Connect more platforms to cross-post.</p>
                  )}
                </div>

                <Button
                  className="schedule-btn"
                  onClick={handleCrossPost}
                  disabled={posting || crossPostPlatforms.length === 0}
                >
                  {posting ? <><FaSpinner className="spinning" /> Posting…</> : `Post to ${crossPostPlatforms.length || ''} Platform${crossPostPlatforms.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      <div className="dashboard-content">
        <div className="content-tabs">
          <div className="tabs-header">
            <div className="tabs-list" role="tablist">
              {[
                { value: 'all',       label: 'All Content', Icon: null },
                { value: 'youtube',   label: 'YouTube',     Icon: FaYoutube },
                { value: 'instagram', label: 'Instagram',   Icon: FaInstagram },
                { value: 'tiktok',    label: 'TikTok',      Icon: FaTiktok },
                { value: 'linkedin',  label: 'LinkedIn',    Icon: FaLinkedin },
                { value: 'twitter',   label: 'X',           Icon: FaTwitter },
                { value: 'threads',   label: 'Threads',     Icon: FaAt },
                { value: 'facebook',  label: 'Facebook',    Icon: FaFacebookF },
              ].map(({ value, label, Icon }) => (
                <button
                  key={value}
                  role="tab"
                  aria-selected={activeTab === value}
                  data-state={activeTab === value ? 'active' : 'inactive'}
                  data-testid={`tab-${value}`}
                  className={`tab-trigger${activeTab === value ? ' active' : ''}`}
                  onClick={() => setActiveTab(value)}
                >
                  {Icon && <Icon />} {label}
                </button>
              ))}
            </div>
            {activeTab === 'all' ? (
              <div className="tabs-actions">
                <button
                  className={`group-toggle-btn ${groupedView ? 'active' : ''}`}
                  onClick={() => setGroupedView(g => !g)}
                  title={groupedView ? 'Grouped: one card per video across platforms' : 'Ungrouped: all platform posts separately'}
                >
                  {groupedView ? <FaLayerGroup /> : <FaList />}
                  <span>{groupedView ? 'Grouped' : 'All Posts'}</span>
                </button>
                <Button
                  variant="outline"
                  onClick={() => connectedPlatforms.filter(p => !['linkedin', 'threads'].includes(p)).forEach(p => syncPlatform(p))}
                  disabled={syncing || connectedPlatforms.length === 0}
                  data-testid="sync-all-btn"
                >
                  <FaSync className={syncing ? 'spinning' : ''} /> Sync All
                </Button>
              </div>
            ) : (
              connectedPlatforms.includes(activeTab) && !['linkedin', 'twitter', 'threads'].includes(activeTab) && (
                <Button
                  variant="outline"
                  onClick={() => syncPlatform(activeTab)}
                  disabled={syncing}
                  data-testid={`sync-${activeTab}-btn`}
                >
                  <FaSync className={syncing ? 'spinning' : ''} /> Sync {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                </Button>
              )
            )}
          </div>

          {/* Content area — rendered directly, no Radix TabsContent to avoid hidden-element layout bugs */}
          <div className="tab-content-area" data-testid={`videos-grid-${activeTab}`}>
            {loading ? (
              <div className="loading-state">Loading…</div>
            ) : filteredVideos.length === 0 ? (
              <div className="empty-state">
                {activeTab === 'all' ? (
                  <p>No videos yet. Connect accounts and sync to get started!</p>
                ) : (() => {
                  const noSync = ['linkedin', 'twitter', 'threads'].includes(activeTab);
                  const label = PLATFORM_META[activeTab]?.label || activeTab;
                  return noSync ? (
                    <>
                      <p>No {label} content posted via Content Radar yet.</p>
                      <p style={{ fontSize: '0.8rem', color: '#888', maxWidth: '340px' }}>
                        {label}'s API doesn't allow reading your posts — this tab shows content you cross-post here through Content Radar.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>No {label} videos found.</p>
                      {connectedPlatforms.includes(activeTab) ? (
                        <Button onClick={() => syncPlatform(activeTab)} data-testid={`sync-${activeTab}-btn`}>
                          <FaSync /> Sync {label}
                        </Button>
                      ) : (
                        <p>Connect your {label} account first.</p>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="videos-grid">{filteredVideos.map(video => <ContentCard key={video.id} video={video} />)}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
